import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaSearch, FaPlus, FaTimes, FaSave, FaFileExcel, FaChevronLeft, FaChevronRight, FaFilter, FaEllipsisV, FaEdit } from 'react-icons/fa';
import './css_pages/ProductsByStore.css';
import {
  getProductsByStoreId, assignMultipleProductsToStore,
  updateProductInStore, removeProductFromStore, importProductsByStore
} from '../services/productsByStoreService';
import { getAllProducts }          from '../services/productsService';
import { BASE_ORDER_BY_OPTIONS }   from '../constants/orderByOptions';
import { useStore }                from '../contexts/StoreContext';
import api                         from '../services/api';

// ── Portal — escapes .main-content overflow
const ModalPortal = ({ children }) => ReactDOM.createPortal(children, document.body);

const ITEMS_PER_PAGE   = 25;
const INITIAL_FILTERS  = { missingFields: false, missingCode: false };

// ── Grouping helpers ──────────────────────────────────────────
const groupByCategory = (items) =>
  items.reduce((acc, item) => {
    const cat = item.categoryName || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item); return acc;
  }, {});

const groupByTypeAndCategory = (items) =>
  items.reduce((acc, item) => {
    const type = item.productTypeName || 'Other';
    const cat  = item.categoryName    || 'Uncategorized';
    if (!acc[type])      acc[type]      = {};
    if (!acc[type][cat]) acc[type][cat] = [];
    acc[type][cat].push(item); return acc;
  }, {});

// ════════════════════════════════════════════════════════════════
function ProductsByStore() {
  const { selectedStore }   = useStore();
  const selectedStoreId     = selectedStore?.id;

  const [productsByStore, setProductsByStore] = useState([]);
  const [products, setProducts]               = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [currentPage, setCurrentPage]         = useState(1);
  const [editMode, setEditMode]               = useState(false);
  const [editedData, setEditedData]           = useState({});
  const [searchTerm, setSearchTerm]           = useState('');
  const [activeFilters, setActiveFilters]     = useState(INITIAL_FILTERS);
  const [groupBy, setGroupBy]                 = useState('category');

  // Modales
  const [showModal, setShowModal]             = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [deleteId, setDeleteId]               = useState(null);

  // Mobile overflow menu
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef(null);

  // Product selection
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch]       = useState('');
  const [productDetails, setProductDetails]     = useState({});

  // Import
  const [importFile, setImportFile]     = useState(null);
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => {
    if (selectedStoreId) { fetchProductsByStore(); setEditMode(false); setCurrentPage(1); }
  }, [selectedStoreId]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, activeFilters]);

  // Close mobile menu on outside click
  useEffect(() => {
    if (!showMobileMenu) return;
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target))
        setShowMobileMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMobileMenu]);

  // ── Fetch ──
  const fetchProducts = async () => {
    try { const r = await getAllProducts(); if (r.success) setProducts(r.data); } catch {}
  };
  const fetchProductsByStore = async () => {
    if (!selectedStoreId) return;
    try {
      setLoading(true); setError(null);
      const r = await getProductsByStoreId(selectedStoreId);
      if (r.success) {
        setProductsByStore(r.data);
        const init = {};
        r.data.forEach(item => { init[item.id] = { par: item.par || '', reorderPoint: item.reorderPoint || '', orderByThe: item.orderByThe || '' }; });
        setEditedData(init);
      }
    } catch { setError('Error loading products'); }
    finally { setLoading(false); }
  };

  // ── GroupBy Toggle ──
  const GroupByToggle = () => (
    <div className="pbs-groupby-toggle">
      <span className="pbs-groupby-label">Group by:</span>
      <button className={`pbs-toggle-btn ${groupBy === 'category' ? 'active' : ''}`} onClick={() => { setGroupBy('category'); setCurrentPage(1); }} type="button" disabled={editMode}>Product Type</button>
      <button className={`pbs-toggle-btn ${groupBy === 'type'     ? 'active' : ''}`} onClick={() => { setGroupBy('type');     setCurrentPage(1); }} type="button" disabled={editMode}>Category</button>
    </div>
  );

  // ── Edit mode ──
  const handleToggleEditMode = () => {
    setShowMobileMenu(false);
    if (editMode) fetchProductsByStore();
    setEditMode(!editMode);
  };
  const handleEditFieldChange = (itemId, field, value) =>
    setEditedData(prev => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));

  const handleSaveBulkChanges = async () => {
    try {
      setLoading(true);
      await Promise.all(Object.keys(editedData).map(itemId => {
        const d = editedData[itemId];
        return updateProductInStore(parseInt(itemId), d.par ? parseFloat(d.par) : null, d.reorderPoint ? parseFloat(d.reorderPoint) : null, d.orderByThe || null);
      }));
      alert('All changes saved successfully');
      setEditMode(false); await fetchProductsByStore();
    } catch { alert('Error saving changes'); }
    finally { setLoading(false); }
  };

  // ── Modal add products ──
  const resetForm = () => { setSelectedProducts([]); setProductSearch(''); setProductDetails({}); };
  const handleOpenModal  = () => {
    setShowMobileMenu(false);
    if (!selectedStoreId) { alert('Please select a store first'); return; }
    resetForm(); setShowModal(true);
  };
  const handleCloseModal = () => { setShowModal(false); resetForm(); };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        setProductDetails(d => { const c = { ...d }; delete c[productId]; return c; });
        return prev.filter(id => id !== productId);
      }
      setProductDetails(d => ({ ...d, [productId]: { par: '', reorderPoint: '', orderByThe: '' } }));
      return [...prev, productId];
    });
  };
  const handleDetailChange = (productId, field, value) =>
    setProductDetails(prev => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));
  const isProductAlreadyInStore = (productId) => productsByStore.some(item => item.productId === productId);

  const handleSelectAll = () => {
    const ids    = availableProducts.map(p => p.id);
    const allSel = ids.every(id => selectedProducts.includes(id));
    if (allSel) {
      setSelectedProducts(prev => prev.filter(id => !ids.includes(id)));
      setProductDetails(prev => { const c = { ...prev }; ids.forEach(id => delete c[id]); return c; });
    } else {
      const newIds = ids.filter(id => !selectedProducts.includes(id));
      setSelectedProducts(prev => [...new Set([...prev, ...ids])]);
      setProductDetails(prev => { const c = { ...prev }; newIds.forEach(id => { c[id] = c[id] || { par: '', reorderPoint: '', orderByThe: '' }; }); return c; });
    }
  };

  const handleSave = async () => {
    if (selectedProducts.length === 0) { alert('Please select at least one product'); return; }
    try {
      setLoading(true);
      const assignResponse = await assignMultipleProductsToStore(selectedProducts, selectedStoreId);
      const successCount   = assignResponse.results.filter(r => r.success).length;
      const errorCount     = assignResponse.results.filter(r => !r.success).length;
      const refreshed      = await getProductsByStoreId(selectedStoreId);
      if (refreshed.success) {
        const updatePromises = [];
        for (const productId of selectedProducts) {
          const detail     = productDetails[productId];
          const hasDetails = detail && (detail.par || detail.reorderPoint || detail.orderByThe);
          if (!hasDetails) continue;
          const storeItem = refreshed.data.find(i => i.productId === productId);
          if (!storeItem) continue;
          updatePromises.push(updateProductInStore(storeItem.id, detail.par ? parseFloat(detail.par) : null, detail.reorderPoint ? parseFloat(detail.reorderPoint) : null, detail.orderByThe || null));
        }
        if (updatePromises.length > 0) {
          await Promise.all(updatePromises);
          const final = await getProductsByStoreId(selectedStoreId);
          if (final.success) {
            setProductsByStore(final.data);
            const init = {}; final.data.forEach(item => { init[item.id] = { par: item.par || '', reorderPoint: item.reorderPoint || '', orderByThe: item.orderByThe || '' }; });
            setEditedData(init);
          }
        } else { setProductsByStore(refreshed.data); }
      }
      if (errorCount === 0) alert(`${successCount} products added successfully`);
      else alert(`${successCount} added, ${errorCount} failed`);
      handleCloseModal();
    } catch { alert('Error adding products'); }
    finally { setLoading(false); }
  };

  // ── Delete ──
  const handleOpenDeleteModal  = (id) => { setDeleteId(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setDeleteId(null); };
  const handleConfirmDelete    = async () => {
    try { setLoading(true); const r = await removeProductFromStore(deleteId); if (r.success) { alert('Product removed successfully'); await fetchProductsByStore(); } handleCloseDeleteModal(); }
    catch { alert('Error deleting'); }
    finally { setLoading(false); }
  };

  // ── Import ──
  const handleOpenImportModal  = () => {
    setShowMobileMenu(false);
    if (!selectedStoreId) { alert('Select a store first'); return; }
    setImportFile(null); setImportResult(null); setShowImportModal(true);
  };
  const handleCloseImportModal = () => { setShowImportModal(false); setImportFile(null); setImportResult(null); };
  const handleImportFileSelect = (e) => {
    const file  = e.target.files[0]; if (!file) return;
    const valid = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet','application/vnd.ms-excel'];
    if (!valid.includes(file.type)) { alert('Please select a valid Excel file (.xlsx or .xls)'); e.target.value = ''; return; }
    setImportFile(file); setImportResult(null);
  };
  const handleConfirmImport = async () => {
    if (!importFile) { alert('Please select a file'); return; }
    try {
      setImporting(true);
      const result = await importProductsByStore(importFile, selectedStoreId);
      setImportResult(result);
      if (result.success) await fetchProductsByStore();
    } catch (err) { alert('Error importing: ' + (err.response?.data?.message || err.message)); }
    finally { setImporting(false); }
  };

  // ── Export ──
  const handleExport = async () => {
    setShowMobileMenu(false);
    if (!selectedStoreId) return;
    try {
      setLoading(true);
      const response = await api.get('/products-by-store/export', { params: { storeId: selectedStoreId }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a   = Object.assign(document.createElement('a'), { href: url, download: `products_${selectedStore.name}_${new Date().toLocaleDateString('en-CA')}.xlsx` });
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { alert('Error exporting: ' + err.message); }
    finally { setLoading(false); }
  };

  // ── Filters & pagination ──
  const toggleFilter    = (key) => setActiveFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const clearFilters    = () => setActiveFilters(INITIAL_FILTERS);
  const anyFilterActive = Object.values(activeFilters).some(Boolean);

  const filteredStoreProducts = productsByStore.filter(item => {
    const matchesSearch = item.productName.toLowerCase().includes(searchTerm.toLowerCase()) || (item.productCode && item.productCode.toLowerCase().includes(searchTerm.toLowerCase()));
    if (!matchesSearch) return false;
    if (activeFilters.missingFields && !(!item.par || !item.reorderPoint || !item.orderByThe)) return false;
    if (activeFilters.missingCode   && item.productCode) return false;
    return true;
  });

  const totalPages      = Math.ceil(filteredStoreProducts.length / ITEMS_PER_PAGE);
  const startIndex      = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentProducts = filteredStoreProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else if (currentPage <= 3)              { pages.push(1,2,3,4,'...',totalPages); }
    else if (currentPage >= totalPages - 2) { pages.push(1,'...',totalPages-3,totalPages-2,totalPages-1,totalPages); }
    else { pages.push(1,'...',currentPage-1,currentPage,currentPage+1,'...',totalPages); }
    return pages;
  };

  const availableProducts     = products.filter(p => (p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.productCode && p.productCode.toLowerCase().includes(productSearch.toLowerCase()))) && !isProductAlreadyInStore(p.id));
  const allAvailableSelected  = availableProducts.length > 0 && availableProducts.every(p => selectedProducts.includes(p.id));

  const getOrderByOptions = (containerType) => {
    const opts = [...BASE_ORDER_BY_OPTIONS];
    if (containerType && !opts.some(o => o.value === containerType)) opts.push({ value: containerType, label: containerType });
    return opts;
  };

  const countMissingFields = productsByStore.filter(i => !i.par || !i.reorderPoint || !i.orderByThe).length;
  const countMissingCode   = productsByStore.filter(i => !i.productCode).length;
  const colCount           = editMode ? 6 : 7;

  // ── Desktop table row renderer ──
  const renderProductRow = (item) => {
    const isWarning = (activeFilters.missingFields && (!item.par || !item.reorderPoint || !item.orderByThe)) || (activeFilters.missingCode && !item.productCode);
    return (
      <tr key={item.id} className={isWarning ? 'pbs-row-highlight-warning' : ''}>
        <td>{item.productName}</td>
        <td className="pbs-code-cell">{item.productCode || <span className="pbs-missing-badge">No code</span>}</td>
        <td className="pbs-container-cell">{item.containerType && item.containerSize ? `${item.containerType} - ${item.containerSize} ${item.containerUnit || ''}` : '—'}</td>
        <td>{editMode ? <input type="number" step="1" className="pbs-inline-edit-input" value={editedData[item.id]?.par || ''} onChange={e => handleEditFieldChange(item.id, 'par', e.target.value)} /> : (item.par || <span className="pbs-missing-badge">—</span>)}</td>
        <td>{editMode ? <input type="number" step="1" className="pbs-inline-edit-input" value={editedData[item.id]?.reorderPoint || ''} onChange={e => handleEditFieldChange(item.id, 'reorderPoint', e.target.value)} /> : (item.reorderPoint || <span className="pbs-missing-badge">—</span>)}</td>
        <td>{editMode ? <select className="pbs-inline-edit-select" value={editedData[item.id]?.orderByThe || ''} onChange={e => handleEditFieldChange(item.id, 'orderByThe', e.target.value)}><option value="">Select...</option>{getOrderByOptions(item.containerType).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select> : (item.orderByThe || <span className="pbs-missing-badge">—</span>)}</td>
        {!editMode && <td><button className="pbs-btn-action pbs-btn-delete" onClick={() => handleOpenDeleteModal(item.id)} disabled={loading}>Remove</button></td>}
      </tr>
    );
  };

  const renderGroupedRows = () => {
    if (currentProducts.length === 0) return <tr><td colSpan={colCount} style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>{loading ? 'Loading…' : 'No products found'}</td></tr>;
    if (groupBy === 'type') {
      const grouped = groupByTypeAndCategory(currentProducts);
      return Object.entries(grouped).map(([typeName, categories]) => (
        <React.Fragment key={typeName}>
          <tr className="pbs-type-row"><td colSpan={colCount}>{typeName}</td></tr>
          {Object.entries(categories).map(([catName, items]) => (
            <React.Fragment key={`${typeName}-${catName}`}>
              <tr className="pbs-category-row pbs-category-sub"><td colSpan={colCount}>&nbsp;&nbsp;&nbsp;{catName}</td></tr>
              {items.map(renderProductRow)}
            </React.Fragment>
          ))}
        </React.Fragment>
      ));
    }
    const grouped = groupByCategory(currentProducts);
    return Object.entries(grouped).map(([catName, items]) => (
      <React.Fragment key={catName}>
        <tr className="pbs-category-row"><td colSpan={colCount}>{catName}</td></tr>
        {items.map(renderProductRow)}
      </React.Fragment>
    ));
  };

  // ── Mobile product card renderer ──
  const renderMobileCard = (item) => {
    const isWarning = (activeFilters.missingFields && (!item.par || !item.reorderPoint || !item.orderByThe)) || (activeFilters.missingCode && !item.productCode);
    const ed = editedData[item.id] || {};
    return (
      <div key={item.id} className={`pbs-product-card${isWarning ? ' pbs-product-card--warn' : ''}`}>
        {/* Name + code */}
        <div className="pbs-product-card-header">
          <div className="pbs-product-card-name">{item.productName}</div>
          {!editMode && (
            <button className="pbs-product-card-remove" onClick={() => handleOpenDeleteModal(item.id)} disabled={loading} title="Remove">
              <FaTimes />
            </button>
          )}
        </div>
        <div className="pbs-product-card-meta">
          {item.productCode
            ? <span className="pbs-product-card-code">{item.productCode}</span>
            : <span className="pbs-missing-badge">No code</span>}
          {item.containerType && item.containerSize && (
            <span className="pbs-product-card-container">{item.containerType} · {item.containerSize} {item.containerUnit || ''}</span>
          )}
        </div>

        {/* Fields: Par | Reorder | Order By */}
        <div className="pbs-product-card-fields">
          <div className="pbs-product-card-field">
            <span className="pbs-product-card-label">Par</span>
            {editMode
              ? <input type="number" step="1" className="pbs-card-edit-input" value={ed.par || ''} onChange={e => handleEditFieldChange(item.id, 'par', e.target.value)} placeholder="—" />
              : <span className="pbs-product-card-value">{item.par || <span className="pbs-missing-badge">—</span>}</span>}
          </div>
          <div className="pbs-product-card-field">
            <span className="pbs-product-card-label">Reorder</span>
            {editMode
              ? <input type="number" step="1" className="pbs-card-edit-input" value={ed.reorderPoint || ''} onChange={e => handleEditFieldChange(item.id, 'reorderPoint', e.target.value)} placeholder="—" />
              : <span className="pbs-product-card-value">{item.reorderPoint || <span className="pbs-missing-badge">—</span>}</span>}
          </div>
          <div className="pbs-product-card-field">
            <span className="pbs-product-card-label">Order By</span>
            {editMode
              ? <select className="pbs-card-edit-select" value={ed.orderByThe || ''} onChange={e => handleEditFieldChange(item.id, 'orderByThe', e.target.value)}>
                  <option value="">Select…</option>
                  {getOrderByOptions(item.containerType).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              : <span className="pbs-product-card-value">{item.orderByThe || <span className="pbs-missing-badge">—</span>}</span>}
          </div>
        </div>
      </div>
    );
  };

  const renderMobileGroupedCards = () => {
    if (currentProducts.length === 0) return <div className="pbs-loading">{loading ? 'Loading…' : 'No products found'}</div>;
    if (groupBy === 'type') {
      const grouped = groupByTypeAndCategory(currentProducts);
      return Object.entries(grouped).map(([typeName, categories]) => (
        <div key={typeName} className="pbs-card-group">
          <div className="pbs-card-group-type">{typeName}</div>
          {Object.entries(categories).map(([catName, items]) => (
            <div key={`${typeName}-${catName}`}>
              <div className="pbs-card-group-cat pbs-card-group-cat--sub">{catName}</div>
              {items.map(renderMobileCard)}
            </div>
          ))}
        </div>
      ));
    }
    const grouped = groupByCategory(currentProducts);
    return Object.entries(grouped).map(([catName, items]) => (
      <div key={catName} className="pbs-card-group">
        <div className="pbs-card-group-cat">{catName}</div>
        {items.map(renderMobileCard)}
      </div>
    ));
  };

  // ════════════════════════════════════════════════════════════════
  return (
    <div className="pbs-page">

      {/* ══ HEADER ════════════════════════════════════════════════════════════ */}
      <div className="pbs-page-header pbs-sticky-header">
        <div className="pbs-header-left">
          <h1 className="pbs-page-title">Products by Store</h1>
          {selectedStore && <span className="pbs-store-badge">{selectedStore.name}</span>}
        </div>

        {/* Desktop actions */}
        <div className="pbs-header-actions pbs-desktop-only">
          {editMode && (
            <button className="pbs-btn-save-bulk" onClick={handleSaveBulkChanges} disabled={loading}>
              <FaSave /> Save All Changes
            </button>
          )}
          <button className="pbs-btn-toggle-edit" onClick={handleToggleEditMode} disabled={loading || !selectedStoreId}>
            {editMode ? 'Cancel Edit Mode' : 'Toggle Edit Mode'}
          </button>
          <button className="pbs-btn-export" onClick={handleExport} disabled={loading || !selectedStoreId || editMode || productsByStore.length === 0}>
            <FaFileExcel /> Export to Excel
          </button>
          <button className="pbs-btn-import" onClick={handleOpenImportModal} disabled={loading || !selectedStoreId || editMode}>
            <FaFileExcel /> Import from Excel
          </button>
          <button className="pbs-btn-primary" onClick={handleOpenModal} disabled={loading || !selectedStoreId || editMode}>
            <FaPlus /> Add products
          </button>
        </div>

        {/* Mobile actions */}
        <div className="pbs-mobile-header-right pbs-mobile-only" ref={mobileMenuRef}>
          {/* In edit mode show Save + Cancel inline */}
          {editMode ? (
            <>
              <button className="pbs-btn-save-bulk pbs-btn-save-mobile" onClick={handleSaveBulkChanges} disabled={loading}>
                <FaSave /> Save
              </button>
              <button className="pbs-btn-toggle-edit pbs-btn-cancel-mobile" onClick={handleToggleEditMode} disabled={loading}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="pbs-btn-primary pbs-btn-add-mobile" onClick={handleOpenModal} disabled={loading || !selectedStoreId}>
                <FaPlus /> Add
              </button>
              <button className="pbs-btn-overflow" onClick={() => setShowMobileMenu(v => !v)} aria-label="More options">
                <FaEllipsisV />
              </button>
              {showMobileMenu && (
                <div className="pbs-overflow-menu">
                  <button className="pbs-overflow-item" onClick={handleToggleEditMode} disabled={loading || !selectedStoreId}>
                    <FaEdit /> Edit Mode
                  </button>
                  <button className="pbs-overflow-item" onClick={handleExport} disabled={loading || !selectedStoreId || productsByStore.length === 0}>
                    <FaFileExcel /> Export to Excel
                  </button>
                  <button className="pbs-overflow-item" onClick={handleOpenImportModal} disabled={loading || !selectedStoreId}>
                    <FaFileExcel /> Import from Excel
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {error && <div className="pbs-error-msg">{error}<button onClick={fetchProductsByStore}>Retry</button></div>}

      {selectedStoreId ? (
        <>
          {/* ── SEARCH + FILTERS ── */}
          <div className="pbs-search-section">
            <div className="pbs-search-bar">
              <input type="text" placeholder="Search by product name or code…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} disabled={editMode} />
              <button className="pbs-btn-search" disabled={editMode}><FaSearch /> Search</button>
            </div>
            <div className="pbs-filter-bar">
              <span className="pbs-filter-label"><FaFilter /> Filters:</span>
              <button className={`pbs-filter-chip ${activeFilters.missingFields ? 'active' : ''}`} onClick={() => toggleFilter('missingFields')} disabled={editMode}>
                Missing Par / Reorder / Order By
                {countMissingFields > 0 && <span className="pbs-filter-chip-count">{countMissingFields}</span>}
              </button>
              <button className={`pbs-filter-chip ${activeFilters.missingCode ? 'active' : ''}`} onClick={() => toggleFilter('missingCode')} disabled={editMode}>
                Missing Code
                {countMissingCode > 0 && <span className="pbs-filter-chip-count">{countMissingCode}</span>}
              </button>
              {anyFilterActive && <button className="pbs-filter-clear" onClick={clearFilters} disabled={editMode}><FaTimes /> Clear filters</button>}
              <div className="pbs-filter-groupby"><GroupByToggle /></div>
            </div>
          </div>

          {/* ── PAGINATION INFO ── */}
          {filteredStoreProducts.length > 0 && (
            <div className="pbs-pagination-info">
              <span>Total: {filteredStoreProducts.length}</span>
              {anyFilterActive && <span className="pbs-filter-active-note">(filtered from {productsByStore.length})</span>}
              <span className="pbs-sep">|</span>
              <span>Showing {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, filteredStoreProducts.length)} of {filteredStoreProducts.length}</span>
            </div>
          )}

          {/* ══ TABLE — desktop ════════════════════════════════════════════ */}
          <div className="pbs-table-wrap pbs-desktop-only">
            {loading && <div className="pbs-loading">Loading…</div>}
            <table className="pbs-data-table">
              <thead>
                <tr>
                  <th>Product</th><th>Product Code</th><th>Container Type/Size</th>
                  <th>Par</th><th>Reorder Point</th><th>Order By</th>
                  {!editMode && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>{renderGroupedRows()}</tbody>
            </table>
          </div>

          {/* ══ CARDS — mobile ═════════════════════════════════════════════ */}
          <div className="pbs-cards-wrap pbs-mobile-only">
            {loading && <div className="pbs-loading">Loading…</div>}
            {!loading && renderMobileGroupedCards()}
          </div>

          {/* ── PAGINATION — desktop full, mobile compact ── */}
          {totalPages > 1 && (
            <>
              {/* Desktop pagination */}
              <div className="pbs-pagination-controls pbs-desktop-only">
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>⟨⟨</button>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.max(p-1,1))} disabled={currentPage === 1}><FaChevronLeft /></button>
                <div className="pbs-pagination-numbers">
                  {getPageNumbers().map((page, i) =>
                    page === '...'
                      ? <span key={`e${i}`} className="pbs-pagination-ellipsis">…</span>
                      : <button key={page} className={`pbs-pagination-number ${currentPage === page ? 'active' : ''}`} onClick={() => setCurrentPage(page)}>{page}</button>
                  )}
                </div>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.min(p+1,totalPages))} disabled={currentPage === totalPages}><FaChevronRight /></button>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>⟩⟩</button>
              </div>

              {/* Mobile pagination — compact prev/next + page indicator */}
              <div className="pbs-pagination-mobile pbs-mobile-only">
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.max(p-1,1))} disabled={currentPage === 1}>
                  <FaChevronLeft /> Prev
                </button>
                <span className="pbs-pagination-mobile-info">Page {currentPage} of {totalPages}</span>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.min(p+1,totalPages))} disabled={currentPage === totalPages}>
                  Next <FaChevronRight />
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="pbs-no-store-msg"><p>Please select a store from the sidebar to view its products</p></div>
      )}

      {/* ══ MODAL ADD PRODUCTS ════════════════════════════════════════════════ */}
      {showModal && (
        <ModalPortal>
          <div className="pbs-modal-overlay">
            <div className="pbs-modal pbs-modal-lg" onClick={e => e.stopPropagation()}>
              <div className="pbs-modal-header">
                <h2>Add products to store</h2>
                <button className="pbs-btn-close" onClick={handleCloseModal}><FaTimes /></button>
              </div>
              <div className="pbs-modal-body">
                <div className="pbs-form-group">
                  <label>Search Products</label>
                  <input type="text" placeholder="Search by name or code…" value={productSearch} onChange={e => setProductSearch(e.target.value)} autoFocus />
                </div>
                {availableProducts.length > 0 && (
                  <div className="pbs-select-all-row">
                    <label className="pbs-select-all-label">
                      <input type="checkbox" checked={allAvailableSelected} onChange={handleSelectAll} />
                      <span>Select all ({availableProducts.length})</span>
                    </label>
                  </div>
                )}
                {selectedProducts.length > 0 && (
                  <div className="pbs-modal-detail-header pbs-desktop-only">
                    <span className="pbs-mdh-name">Product</span>
                    <span className="pbs-mdh-fields"><span>Par</span><span>Reorder Point</span><span>Order By</span></span>
                  </div>
                )}
                <div className="pbs-products-list">
                  {availableProducts.length === 0 ? (
                    <p className="pbs-no-products">No products available to add</p>
                  ) : availableProducts.map(product => {
                    const isSelected = selectedProducts.includes(product.id);
                    const detail     = productDetails[product.id] || {};
                    return (
                      <div key={product.id} className={`pbs-product-item ${isSelected ? 'selected' : ''}`}>
                        <div className="pbs-product-item-main" onClick={() => toggleProductSelection(product.id)}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleProductSelection(product.id)} onClick={e => e.stopPropagation()} />
                          <span className="pbs-product-name">{product.name}</span>
                          {product.productCode && <span className="pbs-product-code-badge">{product.productCode}</span>}
                          <span className="pbs-product-meta">{product.containerType} - {product.containerSize} {product.containerUnit}</span>
                        </div>
                        {isSelected && (
                          <div className="pbs-product-detail-fields" onClick={e => e.stopPropagation()}>
                            <div className="pbs-detail-field"><label>Par</label><input type="number" step="1" min="0" placeholder="—" value={detail.par} onChange={e => handleDetailChange(product.id, 'par', e.target.value)} className="pbs-detail-input" /></div>
                            <div className="pbs-detail-field"><label>Reorder Point</label><input type="number" step="1" min="0" placeholder="—" value={detail.reorderPoint} onChange={e => handleDetailChange(product.id, 'reorderPoint', e.target.value)} className="pbs-detail-input" /></div>
                            <div className="pbs-detail-field"><label>Order By</label><select value={detail.orderByThe} onChange={e => handleDetailChange(product.id, 'orderByThe', e.target.value)} className="pbs-detail-select"><option value="">Select…</option>{getOrderByOptions(product.containerType).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="pbs-selection-summary">{selectedProducts.length} product(s) selected</div>
              </div>
              <div className="pbs-modal-footer">
                <button className="pbs-btn-secondary" onClick={handleCloseModal} disabled={loading}>Cancel</button>
                <button className="pbs-btn-primary" onClick={handleSave} disabled={loading}>
                  {loading ? 'Saving…' : `➕ Add ${selectedProducts.length} product(s)`}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL IMPORT ════════════════════════════════════════════════════ */}
      {showImportModal && (
        <ModalPortal>
          <div className="pbs-modal-overlay">
            <div className="pbs-modal pbs-modal-sm" onClick={e => e.stopPropagation()}>
              <div className="pbs-modal-header">
                <h2>Import from Excel</h2>
                <button className="pbs-btn-close" onClick={handleCloseImportModal}><FaTimes /></button>
              </div>
              <div className="pbs-modal-body">
                <div className="pbs-form-group">
                  <label>Select Excel File *</label>
                  <input type="file" accept=".xlsx,.xls" onChange={handleImportFileSelect} disabled={importing} className="pbs-file-input" />
                  {importFile && <p style={{ marginTop:8, fontSize:13, color:'#64748b' }}>Selected: {importFile.name}</p>}
                </div>
                {importing && <div style={{ display:'flex', justifyContent:'center', padding:'16px' }}><div className="pbs-loading-spinner" /></div>}
                {importResult && (
                  <div className={`pbs-import-result ${importResult.success ? 'success' : 'error'}`}>
                    <p><strong>Import completed!</strong></p>
                    <p>Total rows: {importResult.stats.total}</p>
                    <p>✅ Imported: {importResult.stats.imported}</p>
                    <p>🔄 Updated: {importResult.stats.updated}</p>
                    <p>⏭ Skipped: {importResult.stats.skipped}</p>
                    {importResult.stats.errors && (
                      <div className="pbs-import-errors-list">
                        <p><strong>Errors:</strong></p>
                        {importResult.stats.errors.map((err, i) => <p key={i} className="pbs-import-error-item">• {err}</p>)}
                      </div>
                    )}
                  </div>
                )}
                <div className="pbs-form-hint"><strong>Required columns:</strong> Product Name, Product Code, Par, Reorder Point, Order By</div>
              </div>
              <div className="pbs-modal-footer">
                <button className="pbs-btn-secondary" onClick={handleCloseImportModal} disabled={importing}>{importResult ? 'Close' : 'Cancel'}</button>
                {!importResult && <button className="pbs-btn-primary" onClick={handleConfirmImport} disabled={importing || !importFile}>{importing ? 'Importing…' : 'Import'}</button>}
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* ══ MODAL DELETE ════════════════════════════════════════════════════ */}
      {showDeleteModal && (
        <ModalPortal>
          <div className="pbs-modal-overlay">
            <div className="pbs-modal pbs-modal-sm" onClick={e => e.stopPropagation()}>
              <div className="pbs-modal-header">
                <h2>Confirm deletion</h2>
                <button className="pbs-btn-close" onClick={handleCloseDeleteModal}><FaTimes /></button>
              </div>
              <div className="pbs-modal-body">
                <p style={{ fontSize:14, color:'#374151', marginBottom:8 }}>Are you sure you want to remove this product from the store?</p>
                <p className="pbs-warning-text">This action cannot be undone.</p>
              </div>
              <div className="pbs-modal-footer">
                <button className="pbs-btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>Cancel</button>
                <button className="pbs-btn-danger"    onClick={handleConfirmDelete}    disabled={loading}>{loading ? 'Deleting…' : 'Remove'}</button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default ProductsByStore;